import React, { Suspense } from 'react';
import { Routes, Route, useNavigate, useLocation, Navigate } from 'react-router-dom';
import BaseLayout from '@/components/layout/BaseLayout';
import {
  LayoutDashboard,
  Route as RouteIcon,
  Truck,
  Settings,
  Wallet,
  Users,
  MapPin,
  ClipboardList,
} from 'lucide-react';

// Lazy loaded components for better performance
const Dashboard = React.lazy(() =>
  import('./pages/Dashboard/Dashboard').then(module => ({
    default: module.Dashboard
  }))
);

const VehicleManagement = React.lazy(() =>
  import('./pages/VehicleManagement/VehicleManagement').then(module => ({
    default: module.VehicleManagement
  }))
);

const VehiclePartitionsPage = React.lazy(() =>
  import('@/pages/transport/VehiclePartitionsPage').then(module => ({
    default: module.default
  }))
);

const PaymentsManagement = React.lazy(() =>
  import('./pages/Payments/PaymentsManagement').then(module => ({
    default: module.PaymentsManagement
  }))
);

const Profile = React.lazy(() =>
  import('./pages/Profile/Profile').then(module => ({
    default: module.Profile
  }))
);

const DriverManagement = React.lazy(() =>
  import('./pages/DriverManagement/DriverManagement').then(module => ({
    default: module.default
  }))
);

const CreateTransportRequest = React.lazy(() =>
  import('./pages/TransportRequests/CreateTransportRequest').then(module => ({
    default: module.default
  }))
);

// Unified Delivery Management page (consolidates 4 old pages)
const DeliveryManagement = React.lazy(() =>
  import('./pages/DeliveryManagement/DeliveryManagement').then(module => ({
    default: module.default
  }))
);

// Raw Material Route Management pages
const RouteListPage = React.lazy(() =>
  import('@/pages/transport/RouteListPage').then(module => ({
    default: module.default
  }))
);

const CreateRoutePage = React.lazy(() =>
  import('@/pages/transport/CreateRoutePage').then(module => ({
    default: module.default
  }))
);

const RouteDetailsPage = React.lazy(() =>
  import('@/pages/transport/RouteDetailsPage').then(module => ({
    default: module.default
  }))
);

const ExecutionMonitorPage = React.lazy(() =>
  import('@/pages/transport/ExecutionMonitorPage').then(module => ({
    default: module.default
  }))
);

// Loading component for Suspense
const PageLoader: React.FC = () => (
  <div className="flex items-center justify-center min-h-[400px]">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
    <span className="ml-3 text-gray-600">Loading...</span>
  </div>
);

const TransporterModule: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // Base path for the transporter module
  const basePath = '/transport';

  const navigationItems = [
    {
      id: 'dashboard',
      label: 'Dashboard',
      icon: <LayoutDashboard className="w-5 h-5" />,
      onClick: () => navigate(`${basePath}/dashboard`),
      isActive: location.pathname === `${basePath}/dashboard` || location.pathname === basePath
    },
    {
      id: 'drivers',
      label: 'Driver Management',
      icon: <Users className="w-5 h-5" />,
      onClick: () => navigate(`${basePath}/drivers`),
      isActive: location.pathname === `${basePath}/drivers`
    },
    {
      id: 'delivery-management',
      label: 'Delivery Management',
      icon: <Truck className="w-5 h-5" />,
      onClick: () => navigate(`${basePath}/deliveries`),
      isActive: location.pathname.includes(`${basePath}/deliveries`) ||
                location.pathname.includes(`${basePath}/requests`) ||
                location.pathname === `${basePath}/confirmation` ||
                location.pathname === `${basePath}/history`
    },
    {
      id: 'vehicles',
      label: 'Vehicle Management',
      icon: <Settings className="w-5 h-5" />,
      onClick: () => navigate(`${basePath}/vehicles`),
      isActive: location.pathname === `${basePath}/vehicles`
    },
    {
      id: 'vehicle-partitions',
      label: 'Vehicle Partitions',
      icon: <MapPin className="w-5 h-5" />,
      onClick: () => navigate(`${basePath}/vehicle-partitions`),
      isActive: location.pathname === `${basePath}/vehicle-partitions`
    },
    // HIDDEN: Wallet & Payments - Uncomment when needed
    // {
    //   id: 'wallet',
    //   label: 'Wallet & Payments',
    //   icon: <Wallet className="w-5 h-5" />,
    //   onClick: () => navigate(`${basePath}/wallet`),
    //   isActive: location.pathname === `${basePath}/wallet`
    // },
    {
      id: 'raw-material-routes',
      label: 'Raw Material Routes',
      icon: <MapPin className="w-5 h-5" />,
      onClick: () => navigate(`${basePath}/raw-material/routes`),
      isActive: location.pathname.includes(`${basePath}/raw-material/routes`)
    },
    // HIDDEN: Execution Monitor - Uncomment when needed
    // {
    //   id: 'executions',
    //   label: 'Execution Monitor',
    //   icon: <ClipboardList className="w-5 h-5" />,
    //   onClick: () => navigate(`${basePath}/raw-material/executions`),
    //   isActive: location.pathname.includes(`${basePath}/raw-material/executions`)
    // }
  ];

  // Handler for profile click from the header
  const handleProfileClick = () => {
    navigate(`${basePath}/profile`);
  };

  return (
    <BaseLayout
      moduleName="Transporter"
      moduleSubtitle="Biogas Delivery Management"
      navigationItems={navigationItems}
      userInfo={{
        name: "Transport Manager",
        role: "Operations Head"
      }}
      onProfileClick={handleProfileClick}
    >
      <div className="p-6">
        <Suspense fallback={<PageLoader />}>
          <Routes>
            {/* Default route redirects to dashboard */}
            <Route path="/" element={<Dashboard />} />
            <Route path="/dashboard" element={<Dashboard />} />

            {/* Driver Management Routes */}
            <Route path="/drivers" element={<DriverManagement />} />

            {/*
              Unified Delivery Management
              Consolidates: Transport Requests, Active Deliveries, Delivery Confirmation, Delivery History
            */}
            <Route path="/deliveries" element={<DeliveryManagement />} />
            <Route path="/deliveries/create" element={<CreateTransportRequest />} />

            {/* Legacy routes - redirect to unified Delivery Management */}
            <Route path="/requests" element={<Navigate to={`${basePath}/deliveries`} replace />} />
            <Route path="/requests/create" element={<Navigate to={`${basePath}/deliveries/create`} replace />} />
            <Route path="/requests/:requestId" element={<Navigate to={`${basePath}/deliveries`} replace />} />
            <Route path="/confirmation" element={<Navigate to={`${basePath}/deliveries`} replace />} />
            <Route path="/history" element={<Navigate to={`${basePath}/deliveries`} replace />} />
            <Route path="/delivery-management" element={<Navigate to={`${basePath}/deliveries`} replace />} />

            {/* Vehicle Management */}
            <Route path="/vehicles" element={<VehicleManagement />} />

            {/* Vehicle Partitions Management */}
            <Route path="/vehicle-partitions" element={<VehiclePartitionsPage />} />

            {/* Payments Management & Profile */}
            <Route path="/wallet" element={<PaymentsManagement />} />
            <Route path="/profile" element={<Profile />} />

            {/* Raw Material Route Management */}
            <Route path="/raw-material/routes" element={<RouteListPage />} />
            <Route path="/raw-material/routes/create" element={<CreateRoutePage />} />
            <Route path="/raw-material/routes/:routeId" element={<RouteDetailsPage />} />
            <Route path="/raw-material/executions" element={<ExecutionMonitorPage />} />

            {/* Fallback route */}
            <Route path="*" element={<Dashboard />} />
          </Routes>
        </Suspense>
      </div>
    </BaseLayout>
  );
};

export default TransporterModule;
